import {
  DispositionType,
  EDB,
  EdbDispositionDateType,
  EdbStatus,
  PreparationOrderStatus,
} from "$types";

export const EDB_DISPOSITION_DATE_TYPE_LABELS = {
  [EdbDispositionDateType.EXACT]: "Date exacte",
  [EdbDispositionDateType.AT_LATEST]: "Au plus tard",
};

export const DISPOSITION_LABELS = {
  [DispositionType.ON_DOCKYARD]: "Chantier",
  [DispositionType.NEAREST_WAREHOUSE]: "Entrepôt le plus proche",
  [DispositionType.SUBCONTRACTED_WAREHOUSE]: "Entrepôt sous-traitant",
  [DispositionType.OTHER_PLACE]: "Autre lieu",
  [DispositionType.COLLECTED]: "Collecte",
};

const EDB_STATUS_LABELS: {
  [key in EdbStatus]: string;
} = {
  [EdbStatus.WAITING]: "Approb. en cours",
  [EdbStatus.REFUSED]: "Refusée",
  [EdbStatus.CART_CREATED]: "Panier créé",
  [EdbStatus.CART_VALIDATED]: "Panier validé",
  [EdbStatus.PREPARATION_IN_PROGRESS]: "Préparation en cours",
  [EdbStatus.PREPARATION_AVAILABLE]: "Préparation disponible",
  [EdbStatus.SHIP_IN_PROGRESS]: "Expédié",
  [EdbStatus.DELIVERED_WAREHOUSE]: "Livré",
  [EdbStatus.COLLECTED]: "Collecté",
};

export const getEdbStatusLabel = (
  status: EdbStatus,
  isFilter = false
): string => {
  return status === EdbStatus.CART_CREATED && isFilter
    ? "Réservé"
    : EDB_STATUS_LABELS[status] || "Collecté";
};

export const getDispositionLabels = (isCurrentUserContributor: boolean) => {
  if (isCurrentUserContributor) {
    return {
      [DispositionType.SUBCONTRACTED_WAREHOUSE]:
        DISPOSITION_LABELS[DispositionType.SUBCONTRACTED_WAREHOUSE],
      [DispositionType.COLLECTED]:
        DISPOSITION_LABELS[DispositionType.COLLECTED],
    };
  }
  return DISPOSITION_LABELS;
};

export const getEdbStatusOptions = (
  withDraft = false
): { value: string; label: string }[] => {
  const options = Object.entries(EDB_STATUS_LABELS).map(
    ([key, val]: [key: string, val: string]) => ({
      value: key,
      label: key === EdbStatus.CART_CREATED ? "Réservé" : val,
    })
  );
  if (withDraft) {
    options.unshift({
      value: EdbStatus.CART_CREATED,
      label: "Réservé",
    });
  }
  return options;
};

const EDB_STATUS_COLORS: {
  [key in EdbStatus]: string;
} = {
  [EdbStatus.WAITING]: "blueSapphire",
  [EdbStatus.REFUSED]: "redShimmering",
  [EdbStatus.CART_CREATED]: "blueSapphire",
  [EdbStatus.CART_VALIDATED]: "blueLight2",
  [EdbStatus.PREPARATION_IN_PROGRESS]: "greenWinter",
  [EdbStatus.PREPARATION_AVAILABLE]: "silverPale",
  [EdbStatus.SHIP_IN_PROGRESS]: "pinkMountbatten",
  [EdbStatus.DELIVERED_WAREHOUSE]: "redShimmering",
  [EdbStatus.COLLECTED]: "yellowOrange",
};
export const getEdbStatusColor = (
  status: EdbStatus,
  theme: any,
  disabled?: boolean
): string =>
  theme.color[disabled ? "gray3" : EDB_STATUS_COLORS[status]] || "yellowOrange"; // seemed to be the default color in previous code

const EDB_STATUS_STEPS: {
  [key in EdbStatus]: number;
} = {
  [EdbStatus.WAITING]: -2,
  [EdbStatus.REFUSED]: -2,
  [EdbStatus.CART_CREATED]: -1,
  [EdbStatus.CART_VALIDATED]: 0,
  [EdbStatus.PREPARATION_IN_PROGRESS]: 1,
  [EdbStatus.PREPARATION_AVAILABLE]: 2,
  [EdbStatus.SHIP_IN_PROGRESS]: 3,
  [EdbStatus.DELIVERED_WAREHOUSE]: 4,
  [EdbStatus.COLLECTED]: 5,
};
export const getEdbStatusStep = (status: EdbStatus): number =>
  EDB_STATUS_STEPS[status] || 0; // seemed to be the default step in previous code

/** Same mapping as backend getEdbStatusFromOpStatus. */
const OP_STATUS_TO_EDB_STATUS: { [key: number]: EdbStatus } = {
  [PreparationOrderStatus.CREATED]: EdbStatus.CART_VALIDATED,
  [PreparationOrderStatus.IN_PROGRESS]: EdbStatus.PREPARATION_IN_PROGRESS,
  [PreparationOrderStatus.PREPARED]: EdbStatus.PREPARATION_AVAILABLE,
  [PreparationOrderStatus.TO_SHIP]: EdbStatus.PREPARATION_AVAILABLE,
  [PreparationOrderStatus.SHIPPED]: EdbStatus.SHIP_IN_PROGRESS,
  [PreparationOrderStatus.DELIVERED]: EdbStatus.DELIVERED_WAREHOUSE,
  [PreparationOrderStatus.COLLECTED]: EdbStatus.COLLECTED,
};

/**
 * List / profile badge status = least advanced active OP
 * (aligned with backend getStatusEdbbyOps). WAITING / REFUSED preserved.
 */
export const getEdbStatusFromOps = (
  ops: Array<{ status?: number }> | undefined | null,
  fallbackStatus: EdbStatus
): EdbStatus => {
  if (
    fallbackStatus === EdbStatus.WAITING ||
    fallbackStatus === EdbStatus.REFUSED
  ) {
    return fallbackStatus;
  }

  const activeOps = (ops || []).filter(
    (op) => op.status !== PreparationOrderStatus.CANCELLED
  );

  if (activeOps.length === 0) {
    return fallbackStatus === EdbStatus.CART_CREATED
      ? EdbStatus.CART_CREATED
      : EdbStatus.CART_VALIDATED;
  }

  const minOpStatus = Math.min(
    ...activeOps.map((op) => op.status ?? PreparationOrderStatus.CREATED)
  );

  return OP_STATUS_TO_EDB_STATUS[minOpStatus] ?? fallbackStatus;
};

/**
 * Timeline current step = most advanced active OP.
 * Half-pills handle OPs that have not all reached that status.
 */
export const getEdbTimelineStatusFromOps = (
  ops: Array<{ status?: number }> | undefined | null,
  fallbackStatus: EdbStatus
): EdbStatus => {
  if (
    fallbackStatus === EdbStatus.WAITING ||
    fallbackStatus === EdbStatus.REFUSED
  ) {
    return fallbackStatus;
  }

  const activeOps = (ops || []).filter(
    (op) => op.status !== PreparationOrderStatus.CANCELLED
  );

  if (activeOps.length === 0) {
    return fallbackStatus || EdbStatus.CART_VALIDATED;
  }

  const maxOpStatus = Math.max(
    ...activeOps.map((op) => op.status ?? PreparationOrderStatus.CREATED)
  );

  return OP_STATUS_TO_EDB_STATUS[maxOpStatus] ?? fallbackStatus;
};

/**
 * EDB timeline step (0–5) → OP statusHistory status.
 * Must match DeliverySummary date columns (not raw step index).
 */
export const getOpStatusForEdbTimelineStep = (
  edbStep: number
): PreparationOrderStatus | null => {
  const map: { [step: number]: PreparationOrderStatus } = {
    0: PreparationOrderStatus.CREATED,
    1: PreparationOrderStatus.IN_PROGRESS,
    2: PreparationOrderStatus.PREPARED,
    3: PreparationOrderStatus.SHIPPED,
    4: PreparationOrderStatus.DELIVERED,
    5: PreparationOrderStatus.COLLECTED,
  };
  return map[edbStep] ?? null;
};

export const getDispositionDateTypeLabel = (edb: EDB) =>
  edb.dispositionDateIsExact
    ? EDB_DISPOSITION_DATE_TYPE_LABELS[EdbDispositionDateType.EXACT]
    : EDB_DISPOSITION_DATE_TYPE_LABELS[EdbDispositionDateType.AT_LATEST];

/**
 * @deprecated
 * preparationType must alway be "Préparation pour mise à disposition" --> "Préparation & restockage" is not used anymore
 */
export enum PreparationType {
  SUPPLY = "Préparation pour mise à disposition",
  RESTOCK = "Préparation & restockage",
}

export const getPreparationTypeLabel = (
  preparationType: PreparationType
): string => {
  if (preparationType === PreparationType.SUPPLY) {
    return "Pour mise à dispo.";
  }
  return preparationType;
};

export const preparationTypesOptions = Object.values(PreparationType)
  .map((preparationType) => ({
    value: preparationType,
    label: preparationType,
  }))
  .sort((a1, a2) => a1.label.localeCompare(a2.label));
